import {createPortal, useEffect, useMemo, useRef, useState} from '@wordpress/element';

export function AutocompleteCombo({
                                      placeholder = 'Search…',
                                      multiple = false,
                                      value,
                                      onChange,
                                      fetchPage,
                                      getOptionLabel,
                                      getOptionValue,
                                      renderOption,
                                      disableClear = false,
                                      debounceMs = 300,
                                      usePortal = true,
                                      portalZIndex = 2147483647,
                                  }) {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [page, setPage] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [hasMore, setHasMore] = useState(false);

    const containerRef = useRef(null);
    const inputWrapRef = useRef(null);

    const [menuRect, setMenuRect] = useState(null);

    const optionLabel = useMemo(
        () =>
            getOptionLabel ||
            (opt => {
                if (opt == null) return '';
                if (typeof opt === 'string') return opt;
                return opt.label ?? opt.value ?? String(opt);
            }),
        [getOptionLabel]
    );

    const optionValue = useMemo(
        () =>
            getOptionValue ||
            (opt => {
                if (opt == null) return '';
                if (typeof opt === 'string') return opt;
                return opt.value ?? opt.label ?? JSON.stringify(opt);
            }),
        [getOptionValue]
    );

    const selected = useMemo(() => {
        if (multiple) return Array.isArray(value) ? value : [];
        return value ?? null;
    }, [value, multiple]);

    const selectedIds = useMemo(() => {
        if (!multiple) return selected ? [optionValue(selected)] : [];
        return (selected || []).map(optionValue);
    }, [selected, multiple, optionValue]);

    useEffect(() => {
        function onDocClick(e) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) setOpen(false);
        }

        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), Math.max(0, debounceMs || 0));
        return () => clearTimeout(t);
    }, [query, debounceMs]);

    useEffect(() => {
        let cancelled = false;

        async function load(p, append = false) {
            setLoading(true);
            try {
                const res = await fetchPage(debouncedQuery, p);
                if (cancelled) return;

                setItems(prev => append ? [...prev, ...(res?.items || [])] : (res?.items || []));

                const totalPages = typeof res?.totalPages === 'number' ? res.totalPages : undefined;

                setHasMore(
                    totalPages != null ? p + 1 < totalPages : (res?.items?.length ?? 0) > 0
                );
            } catch (e) {
                if (!cancelled) {
                    setItems([]);
                    setHasMore(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (open) load(page, page > 0);
        return () => {
            cancelled = true;
        };
    }, [debouncedQuery, page, open, fetchPage]);

    const recomputeMenuRect = () => {
        const el = inputWrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const top = r.bottom + 6; // mt-1-ish
        const left = r.left;
        const width = r.width;

        // Keep within viewport if needed (simple clamp)
        const maxHeight = Math.min(288, Math.max(140, window.innerHeight - top - 12)); // 72*4=288
        setMenuRect({top, left, width, maxHeight});
    };

    useEffect(() => {
        if (!open) return;

        recomputeMenuRect();

        // Capture scroll on any ancestor and window
        const onScroll = () => recomputeMenuRect();
        const onResize = () => recomputeMenuRect();

        window.addEventListener('resize', onResize, {passive: true});
        window.addEventListener('scroll', onScroll, {passive: true, capture: true});
        document.addEventListener('scroll', onScroll, {passive: true, capture: true});

        return () => {
            window.removeEventListener('resize', onResize, {capture: false});
            window.removeEventListener('scroll', onScroll, {capture: true});
            document.removeEventListener('scroll', onScroll, {capture: true});
        };
    }, [open]);

    const commitSingle = opt => {
        const clickedId = optionValue(opt);
        const currentId = !multiple && selected ? optionValue(selected) : null;

        if (!multiple && currentId != null && clickedId === currentId) {
            onChange?.(null);
            setOpen(true);
            return;
        }

        onChange?.(opt);
        setOpen(false);
    };

    const commitMultiToggle = opt => {
        const ov = optionValue(opt);
        const curr = Array.isArray(selected) ? selected : [];
        const idx = selectedIds.indexOf(ov);

        if (idx >= 0) {
            const next = curr.filter(it => optionValue(it) !== ov);
            onChange?.(next);
        } else {
            onChange?.([...curr, opt]);
        }
    };

    const removeChip = idx => {
        if (!multiple) {
            onChange?.(null);
            return;
        }
        const next = (selected || []).slice();
        next.splice(idx, 1);
        onChange?.(next);
    };

    const clearQuery = () => {
        setQuery('');
        setDebouncedQuery('');
        setPage(0);
        setItems([]);
    };

    const clearAll = () => {
        if (multiple) onChange?.([]);
        else onChange?.(null);
        clearQuery();
        setOpen(false);
    };

    const onInputFocus = () => setOpen(true);

    const onInputChange = e => {
        setQuery(e.target.value);
        setPage(0);
        setOpen(true);
    };

    const onLoadMore = () => setPage(p => p + 1);

    const MenuContent = (
        <div
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 test-portal shadow-xl bg-white border border-base-100 rounded-xl overflow-y-auto overflow-x-hidden pointer-events-auto"
            style={
                usePortal && menuRect
                    ? {
                        position: 'fixed',
                        top: `${menuRect.top}px`,
                        left: `${menuRect.left}px`,
                        width: `${menuRect.width}px`,
                        maxHeight: `${menuRect.maxHeight}px`,
                        zIndex: portalZIndex,
                    }
                    : undefined
            }
        >
            {loading && items.length === 0 && (
                <div className="p-4 flex items-center gap-2">
                    <span className="loading loading-spinner loading-sm"/>
                    Loading…
                </div>
            )}

            {!loading && items.length === 0 && (
                <div className="p-2 text-sm opacity-70">No results</div>
            )}

            <div className="flex flex-col gap-1">
                {items.map((opt, idx) => {
                    const label = optionLabel(opt);
                    const val = optionValue(opt);
                    const isSelected = selectedIds.includes(val);

                    return (
                        <button
                            key={`${val}-${idx}`}
                            type="button"
                            className={`flex btn btn-ghost items-center justify-between w-full px-4 py-2.5 text-sm font-medium transition-colors rounded-lg group ${
                                isSelected ? 'bg-primary/10 text-primary' : 'pt-dark-blue hover:bg-base-50'
                            }`}
                            aria-pressed={isSelected}
                            onClick={() => multiple ? commitMultiToggle(opt) : commitSingle(opt)}
                        >
                            {renderOption ? (
                                renderOption(opt)
                            ) : (
                                <>
                                    <span>{label}</span>
                                    <div className="flex items-center gap-2">
                                        {typeof opt?.count === 'number' && (
                                            <span
                                                className={`badge badge-sm border-none font-bold ${
                                                    isSelected
                                                        ? 'bg-primary text-white'
                                                        : 'bg-base-200 text-base-content/60'
                                                }`}
                                            >
                                                {opt.count}
                                            </span>
                                        )}
                                        {isSelected && (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4 text-primary"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="3"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </>
                            )}
                        </button>
                    );
                })}
            </div>

            {hasMore && (
                <button
                    type="button"
                    className="btn btn-ghost w-full"
                    onClick={onLoadMore}
                    disabled={loading}
                >
                    {loading ? <span className="loading loading-spinner loading-sm"/> : null}
                    Load more
                </button>
            )}
        </div>
    );

    return (
        <div className="relative w-full" ref={containerRef}>
            {multiple && Array.isArray(selected) && selected.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    {selected.map((s, i) => (
                        <div
                            key={optionValue(s)}
                            className="bg-primary text-white gap-1.5 py-1 px-3 font-bold rounded-full flex items-center text-[10px] uppercase tracking-wider"
                        >
                            <span>{optionLabel(s)}</span>
                            <button
                                className="hover:opacity-80 transition-opacity ml-0.5"
                                onClick={() => removeChip(i)}
                                aria-label="Remove"
                                type="button"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4"
                                          d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="btn btn-ghost btn-xs text-error font-bold hover:bg-error/10 rounded-full"
                        onClick={clearAll}
                        title="Clear selected"
                        aria-label="Clear selected"
                    >
                        Clear
                    </button>
                </div>
            )}

            <div className="relative" ref={inputWrapRef}>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 pt-dark-blue pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none"
                         viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3"
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                </div>

                <input
                    type="text"
                    className="input input-ghost w-full pl-7 pr-8 bg-transparent focus:bg-transparent border-b border-base-200 rounded-none focus:border-primary transition-all px-0 font-medium text-sm"
                    placeholder={placeholder}
                    value={query}
                    onChange={onInputChange}
                    onFocus={onInputFocus}
                />

                {!disableClear && query && (
                    <button
                        type="button"
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary w-6 h-6 flex items-center justify-center transition-colors"
                        onClick={clearQuery}
                        aria-label="Clear"
                        title="Clear"
                    >
                        <span className="text-lg">✕</span>
                    </button>
                )}
            </div>

            {open &&
                (usePortal
                    ? createPortal(MenuContent, document.body)
                    : (
                        <div className="absolute w-full mt-1">
                            <div className="max-h-72">{MenuContent}</div>
                        </div>
                    ))}
        </div>
    );
}